import {MessageDescriptor} from '@common/i18n/message-descriptor';
import {editorState} from '@app/editor/state/editor-store';
import {toast} from '@common/ui/toast/toast';
import {message} from '@common/i18n/message';

export const MAX_PERSISTED_ENTRIES = 50;

export interface PersistedHistoryEntry {
  uniqueId: string;
  createdAt: number;
  displayName: MessageDescriptor;
  pageName: string;
  afterHtml: string;
}

export interface PersistedHistory {
  initialPages: Record<string, string>; // pageName -> innerHTML
  entries: PersistedHistoryEntry[];
  pointer: number;
}

// --- IndexedDB wrapper ---

const DB_NAME = 'landed-editor';
const DB_VERSION = 1;
const STORE_NAME = 'history';

let dbPromise: Promise<IDBDatabase> | null = null;

function getDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
  });
  return dbPromise;
}

function idbGet(key: string): Promise<PersistedHistory | null> {
  return getDb().then(
    db =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const req = tx.objectStore(STORE_NAME).get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => reject(req.error);
      }),
  );
}

function idbSet(key: string, value: PersistedHistory): Promise<void> {
  return getDb().then(
    db =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const req = tx.objectStore(STORE_NAME).put(value, key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      }),
  );
}

function idbDelete(key: string): Promise<void> {
  return getDb().then(
    db =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const req = tx.objectStore(STORE_NAME).delete(key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      }),
  );
}

// --- Debounced save ---

let pendingHistory: PersistedHistory | null = null;
let pendingProjectId: number | string | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let hasWarnedQuota = false;

function flushSave() {
  if (pendingHistory && pendingProjectId) {
    const key = `project-${pendingProjectId}`;
    const data = pendingHistory;
    pendingHistory = null;
    pendingProjectId = null;
    idbSet(key, data).catch(() => {
      if (!hasWarnedQuota) {
        hasWarnedQuota = true;
        toast.danger(
          message(
            'History storage is full. Older entries may not be restored after refresh.',
          ),
        );
      }
    });
  }
}

// Flush on tab close / refresh so we don't lose the latest state
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushSave);
}

// --- Public API ---

export function saveHistory(
  projectId: number | string,
  history: PersistedHistory,
): void {
  // Cap entries to MAX_PERSISTED_ENTRIES, keeping the most recent
  if (history.entries.length > MAX_PERSISTED_ENTRIES) {
    const trimCount = history.entries.length - MAX_PERSISTED_ENTRIES;
    history.entries = history.entries.slice(trimCount);
    history.pointer = Math.max(-1, history.pointer - trimCount);
  }

  pendingHistory = history;
  pendingProjectId = projectId;

  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(flushSave, 2000);
}

export async function loadHistory(
  projectId: number | string,
): Promise<PersistedHistory | null> {
  try {
    return await idbGet(`project-${projectId}`);
  } catch {
    return null;
  }
}

export async function clearHistory(projectId: number | string): Promise<void> {
  // Cancel any pending debounced save so it doesn't re-populate after clear
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  pendingHistory = null;
  pendingProjectId = null;
  try {
    await idbDelete(`project-${projectId}`);
  } catch {
    // ignore
  }
}

/**
 * Capture the current innerHTML of all pages as the initial snapshot.
 */
export function captureInitialPages(): Record<string, string> {
  const project = editorState().project;
  if (!project) return {};
  const result: Record<string, string> = {};
  for (const page of project.pages) {
    if (page.doc) {
      result[page.name] = page.doc.documentElement.innerHTML;
    }
  }
  return result;
}

/**
 * Get the innerHTML of the page that a mutation affected.
 */
export function capturePageHtml(pageId: string): {
  pageName: string;
  html: string;
} | null {
  const project = editorState().project;
  if (!project) return null;
  const page = project.pages.find(p => p.id === pageId);
  if (!page?.doc) return null;
  return {pageName: page.name, html: page.doc.documentElement.innerHTML};
}
